// src/lib/services/uploadService.ts
import { supabase } from '../../supabaseClient';
import { validateFileUpload } from '../../utils/validation';
import { compressImage as compressImageUtil } from '../../utils/image';
import type { UploadOptions, UploadService } from '../../../types';

// === SECURE MEDIA UPLOAD HELPERS ===

/**
 * Securely uploads media with validation and compression
 * @param {File} file - The file to upload
 * @param {string} folder - Storage folder (default: 'uploads')
 * @param {object} options - Upload options
 * @returns {Promise<string>} - Public URL of uploaded file
 */
async function uploadMedia(
  file: File,
  folder: string = 'uploads',
  options: UploadOptions = {}
): Promise<string> {
  const {
    maxSizeMB = 10,
    allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    allowedExtensions = ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
    compress = true,
    maxWidth = 1200,
    maxHeight = 800,
    quality = 0.8,
  } = options;

  let processedFile: File = file;

  // Compress image FIRST if it's an image and compression is enabled
  // This allows large images to be compressed before size validation
  if (compress && file.type.startsWith('image/')) {
    try {
      processedFile = await compressImageUtil(file, maxWidth, maxHeight, quality);
    } catch (compressionError) {
      console.warn('Image compression failed, using original file:', compressionError);
      // Continue with original file if compression fails
      processedFile = file;
    }
  }

  // Validate file AFTER compression (validates the compressed size)
  const validation = validateFileUpload(processedFile, {
    maxSizeMB,
    allowedTypes,
    allowedExtensions,
  });

  if (!validation.isValid) {
    throw new Error(validation.error ?? 'File validation failed');
  }

  // Verify file content matches an actual image format (magic bytes)
  const hasValidSignature = await validateMagicBytes(processedFile);
  if (!hasValidSignature) {
    throw new Error('File content does not match a supported image format');
  }

  // Generate secure filename
  const fileExt = getSecureFileExtension(processedFile.name);
  const timestamp = Date.now();
  const randomId = generateSecureId();
  const sanitizedFolder = sanitizeFilePath(folder);
  const fileName = `${sanitizedFolder}/${timestamp}-${randomId}.${fileExt}`;

  // Upload to Supabase
  const { error } = await supabase.storage.from('community-media').upload(fileName, processedFile, {
    cacheControl: '3600',
    upsert: false, // Prevent overwriting existing files
  });

  if (error) {
    console.error('Upload error:', error);
    throw new Error('Upload failed. Please try again.');
  }

  // Get public URL
  const {
    data: { publicUrl },
  } = supabase.storage.from('community-media').getPublicUrl(fileName);

  return publicUrl;
}

/**
 * Uploads profile avatar with specific validation
 * @param {File} file - Avatar image file
 * @param {string} userId - User ID for folder organization
 * @returns {Promise<string>} - Public URL of uploaded avatar
 */
async function uploadAvatar(file: File, userId: string): Promise<string> {
  if (!userId) {
    throw new Error('User ID is required for avatar upload');
  }

  return uploadMedia(file, `avatars/${sanitizeFilePath(userId)}`, {
    maxSizeMB: 5,
    allowedTypes: ['image/jpeg', 'image/png', 'image/webp'],
    allowedExtensions: ['.jpg', '.jpeg', '.png', '.webp'],
    compress: true,
    maxWidth: 400,
    maxHeight: 400,
    quality: 0.9,
  });
}

/**
 * Uploads journal image with specific validation
 * @param {File} file - Journal image file
 * @param {string} userId - User ID for folder organization
 * @returns {Promise<string>} - Public URL of uploaded image
 */
async function uploadJournalImage(file: File, userId: string): Promise<string> {
  if (!userId) {
    throw new Error('User ID is required for journal image upload');
  }

  return uploadMedia(file, `journal/${sanitizeFilePath(userId)}`, {
    maxSizeMB: 8,
    allowedTypes: ['image/jpeg', 'image/png', 'image/gif', 'image/webp'],
    allowedExtensions: ['.jpg', '.jpeg', '.png', '.gif', '.webp'],
    compress: true,
    maxWidth: 1200,
    maxHeight: 800,
    quality: 0.85,
  });
}

/**
 * Deletes a file from storage
 * @param {string} filePath - Path to the file to delete
 * @returns {Promise<boolean>} - Success status
 */
async function deleteMedia(filePath: string): Promise<boolean> {
  if (!filePath || typeof filePath !== 'string') {
    throw new Error('Valid file path is required');
  }

  // Extract file path from URL if needed
  const pathFromUrl = extractFilePathFromUrl(filePath);

  const { error } = await supabase.storage.from('community-media').remove([pathFromUrl]);

  if (error) {
    console.error('Delete error:', error);
    throw new Error('Failed to delete file');
  }

  return true;
}

// === MAGIC BYTE VALIDATION ===

/** Known image file signatures (magic bytes) */
const IMAGE_SIGNATURES: Array<{ mime: string; bytes: number[] }> = [
  { mime: 'image/jpeg', bytes: [0xFF, 0xD8, 0xFF] },
  { mime: 'image/png', bytes: [0x89, 0x50, 0x4E, 0x47] },
  { mime: 'image/gif', bytes: [0x47, 0x49, 0x46, 0x38] },       // GIF87a / GIF89a
  { mime: 'image/webp', bytes: [0x52, 0x49, 0x46, 0x46] },       // RIFF (WebP container)
];

/**
 * Validates that the file's actual bytes match an allowed image format.
 * Prevents attacks where a malicious file is renamed with an image extension.
 */
async function validateMagicBytes(file: File): Promise<boolean> {
  const maxHeaderSize = 12; // Enough bytes for all checks
  const buffer = await file.slice(0, maxHeaderSize).arrayBuffer();
  const header = new Uint8Array(buffer);

  return IMAGE_SIGNATURES.some(sig =>
    sig.bytes.every((byte, i) => header[i] === byte)
  );
}

// === HELPER FUNCTIONS ===

/**
 * Gets secure file extension from filename
 * @param {string} filename - Original filename
 * @returns {string} - Sanitized file extension
 */
function getSecureFileExtension(filename: string): string {
  if (!filename || typeof filename !== 'string') {
    return 'bin';
  }

  const ext = filename.toLowerCase().split('.').pop();

  // Only allow specific safe extensions
  const allowedExtensions = ['jpg', 'jpeg', 'png', 'gif', 'webp'];

  return ext && allowedExtensions.includes(ext) ? ext : 'bin';
}

/**
 * Generates a cryptographically secure ID
 * @returns {string} - Secure random ID
 */
function generateSecureId(): string {
  const array = new Uint8Array(16);
  crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
}

/**
 * Sanitizes file path to prevent directory traversal
 * @param {string} path - File path to sanitize
 * @returns {string} - Sanitized path
 */
function sanitizeFilePath(path: string): string {
  if (!path || typeof path !== 'string') {
    return 'uploads';
  }

  return (
    path
      .replace(/[^a-zA-Z0-9\-_]/g, '') // Only allow alphanumeric, hyphens, underscores
      .replace(/\.+/g, '') // Remove dots (prevent directory traversal)
      .replace(/\/+/g, '') // Remove slashes
      .substring(0, 50) || // Limit length
    'uploads'
  ); // Fallback if sanitization results in empty string
}

/**
 * Extracts file path from Supabase public URL
 * @param {string} url - Public URL
 * @returns {string} - File path
 */
function extractFilePathFromUrl(url: string): string {
  if (!url || typeof url !== 'string') {
    return '';
  }

  // Handle both full URLs and relative paths
  if (url.includes('/storage/v1/object/public/community-media/')) {
    return url.split('/storage/v1/object/public/community-media/')[1];
  }

  // If it's already a path, return as-is
  return url;
}

// Create the uploadService object that implements the UploadService interface
const uploadService: UploadService = {
  uploadMedia,
  uploadAvatar,
  uploadJournalImage,
  deleteMedia,
};

export { uploadMedia, uploadAvatar, uploadJournalImage, deleteMedia, uploadService };
